'use client';

import { Button, InfiniteList } from '@components/common';
import { useSwrWorkplaceAlumni } from '@hooks/useSwrApi/endpoints/workplace';
import { useTranslation } from 'next-i18next';
import { TalentCardMemo } from '@components/templates/employer/cards-lists/TalentCard';
import { commonStyles } from '../common-styles';

// Interface
// ************************************
export interface AlumniListProps {
  slug: string,
  locale: Locale,
  alumniFromServer?: ApiSchema<'ResumeResult2'>,
}

/**
 * @description Alumni List component. Renders a list of employer alumni cards.
 *
 * - **slug** - string. Employer slug.
 * - **locale** - Locale. The current app locale.
 * - **alumniFromServer** - ApiSchema<'ResumeResult2'>. The data for alumni list.
 */
export const AlumniListInfinite = (props: AlumniListProps) => {
  // Destructure props
  const {
    slug, locale, alumniFromServer,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // SWR - Alumni
  // ******************************
  const {
    data,
    size,
    sizeAsRef,
    setSize,
    isLoading,
    isEmpty,
    showLoadMore,
    isValidating,
  } = useSwrWorkplaceAlumni({
    slug,
    locale,
    startIndex: 1,
    serverSideData: alumniFromServer,
  });

  // Render component
  // *****************************************
  return (
    <InfiniteList
      autoload={1}
      infiniteProps={{
        setSize, isEmpty, showLoadMore, sizeAsRef, isLoading,
      }}
      renderList={() => (
        <div className={commonStyles.grid}>
          {data?.map((pageGroup) => (
            pageGroup?.items?.map((alumniCardData) => (
              <TalentCardMemo
                trackingData={{
                  listType: 'workplace',
                }}
                locale={locale}
                key={alumniCardData.slug}
                cardData={alumniCardData}
                trackingId={pageGroup?.apiRequestId}
                mode="alumni"
              />
            ))
          ))}
        </div>
      )}
      renderButton={() => (
        <div className="mt-3 text-center">
          <Button
            isLoading={isValidating}
            size="sm"
            color="white"
            rounding="full"
            className={commonStyles.showMoreButton}
            onClick={() => { void setSize(size + 1); }}
          >
            {t('employer.detail.alumni.see_more')}
          </Button>
        </div>
      )}
    />
  );
};
